import CloseIcon from '@mui/icons-material/Close'
import {Box, DialogContent, DialogTitle, IconButton, Typography} from '@mui/material'
import {t} from 'i18next'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useShipToCreationContext} from '@hconnect/tuc-processes/shipToCreation'
import {GoogleMap} from '../../OrderIntake/components/GoogleMap'
import {MapMarker} from '../../OrderIntake/components/MapMarker'
import {AddressSearch} from './components/AddressSearch'
import {useAddressManager} from './hooks/useAddressManager'
import {styles} from './styles'
import {UserLocationButton} from './components/UserLocationButton'
import {DEFAULT_MAP_ZOOM, EMPTY_MAP_ZOOM} from './constants'
import _ from 'lodash'
import {getCoordinatesDistance} from '@hconnect/tuc-processes/utils/geocode'

export const JobSiteCreationMap: React.FC<{onClose: () => void; onConfirm: () => void}> = ({
  onClose,
  onConfirm
}) => {
  const {
    processData,
    shipToCreationForm: {watch, setValue}
  } = useShipToCreationContext()

  const [jobSiteAddress] = watch(['jobSiteAddress'])

  const {
    marker,
    searchBarText,
    suggestions,
    isLoading,
    isMarkerLoading,
    canConfirm,
    markerLocationName,
    handleSearchChange,
    handleMapClick,
    handleSuggestionSelect,
    getSelectedAddressDetails,
    resetAll,
    showResultList,
    locationToReverse
  } = useAddressManager({
    country: processData!.country,
    initialAddress: jobSiteAddress
  })

  const [zoom, setZoom] = useState<number>(marker ? DEFAULT_MAP_ZOOM : EMPTY_MAP_ZOOM)
  const [animatedMarkerPosition, setAnimatedMarkerPosition] = useState<{
    latitude: number
    longitude: number
  } | null>(marker)
  const animationRef = useRef<number>()
  const [center, setCenter] = useState<{lat: number; lng: number} | undefined>(
    marker ? {lat: marker.latitude, lng: marker.longitude} : undefined
  )

  const [addressSnapped, setAddressSnapped] = useState<boolean>(false)

  useEffect(() => {
    if (!locationToReverse || !marker || _.isEqual(locationToReverse, marker)) {
      setAddressSnapped(false)
      return
    }
    const isSnapped = !_.isEqual(locationToReverse, marker)

    setAddressSnapped(isSnapped)

    if (isSnapped) {
      const timer = setTimeout(() => setAddressSnapped(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [locationToReverse, marker])

  useEffect(() => {
    if (marker && !center) {
      setCenter({lat: marker.latitude, lng: marker.longitude})
      setZoom(DEFAULT_MAP_ZOOM)
    }
  }, [marker, center])

  const animateMarker = useCallback(
    (
      from: {latitude: number; longitude: number},
      to: {latitude: number; longitude: number},
      duration: number,
      onEnd?: () => void
    ) => {
      const startTime = Date.now()

      const animate = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        const easedProgress = 1 - Math.pow(1 - progress, 3)

        const currentLat = from.latitude + (to.latitude - from.latitude) * easedProgress
        const currentLng = from.longitude + (to.longitude - from.longitude) * easedProgress

        setAnimatedMarkerPosition({
          latitude: currentLat,
          longitude: currentLng
        })

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        } else {
          onEnd?.()
        }
      }
      animationRef.current = requestAnimationFrame(animate)
    },
    []
  )

  useEffect(() => {
    if (!locationToReverse) {
      if (marker) {
        setAnimatedMarkerPosition(marker)
        setCenter({lat: marker.latitude, lng: marker.longitude})
        setZoom(DEFAULT_MAP_ZOOM)
      }
      return
    }

    if (!marker) {
      setAnimatedMarkerPosition(locationToReverse)
      return
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
    animateMarker(locationToReverse!, marker!, 1000)
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [locationToReverse, marker, animateMarker])

  useEffect(() => {
    if (marker && zoom !== DEFAULT_MAP_ZOOM) {
      setZoom(DEFAULT_MAP_ZOOM)
    }
  }, [marker])

  const onConfirmLocation = useCallback(() => {
    const addressDetails = getSelectedAddressDetails()
    if (!addressDetails) return

    setValue('jobSiteAddress', addressDetails, {shouldValidate: true, shouldDirty: true})
    onConfirm()
  }, [getSelectedAddressDetails, setValue, onConfirm])

  const onClearAll = () => {
    resetAll()
    setValue('jobSiteAddress', undefined, {shouldValidate: true, shouldDirty: true})
  }

  return (
    <>
      <DialogTitle
        sx={{...styles.dialogTitle, ...styles.mapDialogTitle}}
        data-test-id="map-dialog-title"
      >
        <Typography
          variant="h3"
          component="div"
          sx={styles.titleText}
          data-test-id="map-dialog-title-text"
        >
          {t('orderIntake.rmc.jobSiteModal.create.mapTitle')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
          data-test-id="map-dialog-close-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={styles.mapSubtitle} data-test-id="map-subtitle-container">
        <Typography variant="body1" sx={{color: 'text.secondary'}} data-test-id="map-subtitle-text">
          {t('orderIntake.rmc.jobSiteModal.create.mapSubtitle')}
        </Typography>
      </Box>

      <DialogContent sx={styles.dialogContent} data-test-id="map-dialog-content">
        <Box sx={{...styles.sectionContainer, ...styles.mapContainer}} data-test-id="map-container">
          <GoogleMap
            center={center}
            height="100%"
            width="100%"
            zoom={zoom}
            onClick={handleMapClick}
            yesIWantToUseGoogleMapApiInternals={true}
            data-test-id="job-site-google-map"
            onGoogleApiLoaded={({map, maps}) => {
              const useLocationButton = UserLocationButton((pos) => {
                map.setCenter(pos)
                map.setZoom(DEFAULT_MAP_ZOOM)
                handleMapClick(pos)
              })
              map.controls[maps.ControlPosition.RIGHT_BOTTOM].push(useLocationButton)
            }}
          >
            {addressSnapped && (
              <MapMarker
                lat={locationToReverse!.latitude}
                lng={locationToReverse!.longitude}
                isLoading={isMarkerLoading}
                data-test-id="job-site-map-marker-ghost-pin"
                outLine
              />
            )}
            {animatedMarkerPosition && (
              <MapMarker
                lat={animatedMarkerPosition.latitude}
                lng={animatedMarkerPosition.longitude}
                isLoading={isMarkerLoading}
                title={markerLocationName}
                data-test-id="job-site-map-marker"
              />
            )}
          </GoogleMap>

          <AddressSearch
            searchValue={searchBarText}
            onSearchChange={handleSearchChange}
            suggestions={suggestions}
            isLoading={isLoading}
            isMarkerLoading={isMarkerLoading}
            onSuggestionSelect={handleSuggestionSelect}
            canConfirm={canConfirm}
            onConfirm={onConfirmLocation}
            onClearSelection={onClearAll}
            showResultList={showResultList}
            data-test-id="job-site-address-search"
            addressSnapped={addressSnapped}
          />
        </Box>
      </DialogContent>
    </>
  )
}
